// js/Component/AddressBook/SharedAddressBooksView.jsx
//
// Read-only management view of every address book this device can ACCESS but
// does not OWN — i.e. books shared to it directly or via a device group.
// The owner-managed "Home Page Contacts" (default book) is excluded here; it
// has its own tab. From here an admin can copy any shared contact into the
// device's Home Page Contacts (favourites).

import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import deviceProfile from "images/device.png";

import {
    fetchAddressBooksForDevice,
    fetchAddressBookEntries,
    copyOrgEntriesToBook,
} from "js/Api/index";

function isDefaultBook(b) {
    return b?.systemKey === "default" || b?.bookType === "system_default";
}

function PermissionBadge({ permission }) {
    const editable = permission === "editable";
    return (
        <span
            style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 20,
                color: editable ? "#b45309" : "#1d4ed8",
                background: editable ? "#fef3c7" : "#dbeafe",
            }}
        >
            {editable ? "Editable" : "Read only"}
        </span>
    );
}

function SharedAddressBooksView({ deviceId, searchQuery = "" }) {
    const [loading, setLoading] = useState(false);
    const [defaultBook, setDefaultBook] = useState(null);
    const [sharedBooks, setSharedBooks] = useState([]);

    // bookId -> { loading, entries }
    const [entriesByBook, setEntriesByBook] = useState({});
    const [expandedId, setExpandedId] = useState(null);
    const [copyingId, setCopyingId] = useState(null);

    const loadBooks = async () => {
        if (!deviceId) return;
        setLoading(true);
        try {
            const res = await fetchAddressBooksForDevice(deviceId);
            if (!res.success) {
                toast.error(res.error || "Failed to load address books");
                setDefaultBook(null);
                setSharedBooks([]);
                return;
            }
            const books = Array.isArray(res.data) ? res.data : [];
            // Home Page Contacts = the device's own default book (copy target).
            const home =
                books.find((b) => b.accessType === "owned" && isDefaultBook(b)) ||
                books.find((b) => isDefaultBook(b)) ||
                null;
            setDefaultBook(home);
            setSharedBooks(books.filter((b) => b.accessType === "shared"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBooks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId]);

    const loadEntries = async (bookId) => {
        setEntriesByBook((prev) => ({
            ...prev,
            [bookId]: { loading: true, entries: prev[bookId]?.entries || [] },
        }));
        const res = await fetchAddressBookEntries(bookId);
        const list = res.success && Array.isArray(res.data)
            ? res.data.filter((e) => e.isActive !== false)
            : [];
        if (!res.success) toast.error(res.error || "Failed to load contacts");
        setEntriesByBook((prev) => ({
            ...prev,
            [bookId]: { loading: false, entries: list },
        }));
    };

    const toggleExpand = (bookId) => {
        if (expandedId === bookId) {
            setExpandedId(null);
            return;
        }
        setExpandedId(bookId);
        if (!entriesByBook[bookId]) loadEntries(bookId);
    };

    const handleCopyToHome = async (entry) => {
        if (!defaultBook?.id) {
            toast.error("No Home Page Contacts book found for this device.");
            return;
        }
        setCopyingId(entry.id);
        const res = await copyOrgEntriesToBook(defaultBook.id, [entry]);
        setCopyingId(null);
        if (res.success) {
            toast.success(`"${entry.displayName}" added to Home Page Contacts`);
        } else {
            toast.error(res.error || "Failed to copy contact");
        }
    };

    const q = (searchQuery || "").trim().toLowerCase();

    const filteredBooks = useMemo(() => {
        if (!q) return sharedBooks;
        // Keep a book if its name matches or any loaded entry matches.
        return sharedBooks.filter((b) => {
            if ((b.name || "").toLowerCase().includes(q)) return true;
            const loaded = entriesByBook[b.id]?.entries || [];
            return loaded.some(
                (e) =>
                    (e.displayName || "").toLowerCase().includes(q) ||
                    (e.phoneNumberRaw || "").toLowerCase().includes(q)
            );
        });
    }, [sharedBooks, entriesByBook, q]);

    const filterEntries = (entries) => {
        if (!q) return entries;
        return entries.filter(
            (e) =>
                (e.displayName || "").toLowerCase().includes(q) ||
                (e.phoneNumberRaw || "").toLowerCase().includes(q)
        );
    };

    if (!deviceId) {
        return <div style={{ padding: 20 }}><p>No device selected.</p></div>;
    }

    return (
        <div style={{ paddingTop: 10 }}>
            <div
                style={{
                    background: "#f1f5ff",
                    border: "1px solid #d6e0ff",
                    borderRadius: 6,
                    padding: "10px 14px",
                    marginBottom: 16,
                    fontSize: 13,
                    color: "#334",
                }}
            >
                Address books shared to this device (directly or via a device group).
                On the device these are opened from a separate screen. Use{" "}
                <strong>Add to Home Page</strong> to copy a contact into the device's
                Home Page Contacts (one-tap favourites).
            </div>

            {loading ? (
                <div style={{ padding: "20px 0", textAlign: "center" }}>
                    <i
                        className="fa fa-spinner fa-spin"
                        style={{ fontSize: 24, marginRight: 8, color: "#007bff" }}
                    />
                    <p style={{ marginTop: 10, color: "#666" }}>
                        Loading shared address books...
                    </p>
                </div>
            ) : sharedBooks.length === 0 ? (
                <div
                    style={{
                        textAlign: "center",
                        padding: 30,
                        border: "1px dashed #ddd",
                        borderRadius: 6,
                        color: "#666",
                    }}
                >
                    <p style={{ margin: 0 }}>
                        No address books are shared to this device.
                    </p>
                </div>
            ) : filteredBooks.length === 0 ? (
                <div
                    style={{
                        textAlign: "center",
                        padding: 30,
                        border: "1px dashed #ddd",
                        borderRadius: 6,
                        color: "#666",
                    }}
                >
                    <p style={{ margin: 0 }}>No shared books match "{q}".</p>
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {filteredBooks.map((book) => {
                        const expanded = expandedId === book.id;
                        const state = entriesByBook[book.id];
                        const entries = filterEntries(state?.entries || []);
                        return (
                            <div
                                key={book.id}
                                style={{
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 8,
                                    overflow: "hidden",
                                }}
                            >
                                {/* Book header row */}
                                <div
                                    onClick={() => toggleExpand(book.id)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        padding: "12px 16px",
                                        cursor: "pointer",
                                        background: expanded ? "#f8fafc" : "#fff",
                                    }}
                                >
                                    <i
                                        className={`fa fa-chevron-${expanded ? "down" : "right"}`}
                                        style={{ color: "#999", fontSize: 12, width: 12 }}
                                    />
                                    <span style={{ fontWeight: 600, flex: 1 }}>
                                        {book.name}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            padding: "2px 8px",
                                            borderRadius: 20,
                                            color: "#6b21a8",
                                            background: "#f3e8ff",
                                        }}
                                    >
                                        Shared
                                    </span>
                                    <PermissionBadge permission={book.permission} />
                                    <span style={{ fontSize: 12, color: "#888" }}>
                                        {book.entryCount ?? 0} contact
                                        {book.entryCount !== 1 ? "s" : ""}
                                    </span>
                                </div>

                                {/* Entries */}
                                {expanded && (
                                    <div style={{ borderTop: "1px solid #eee" }}>
                                        {state?.loading ? (
                                            <div
                                                style={{
                                                    padding: 18,
                                                    textAlign: "center",
                                                    color: "#666",
                                                }}
                                            >
                                                <i
                                                    className="fa fa-spinner fa-spin"
                                                    style={{ marginRight: 8, color: "#007bff" }}
                                                />
                                                Loading contacts...
                                            </div>
                                        ) : entries.length === 0 ? (
                                            <div
                                                style={{
                                                    padding: 18,
                                                    textAlign: "center",
                                                    color: "#888",
                                                }}
                                            >
                                                {(state?.entries || []).length === 0
                                                    ? "This book has no contacts."
                                                    : `No contacts match "${q}".`}
                                            </div>
                                        ) : (
                                            <div style={{ overflowX: "auto" }}>
                                                <table
                                                    className="table table-hover"
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th>Contact</th>
                                                            <th>Type</th>
                                                            <th>Phone</th>
                                                            <th style={{ width: 180 }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {entries.map((e) => {
                                                            const avatarSrc = e.avatarUrl
                                                                ? `${e.avatarUrl}${e.avatarUpdatedAt
                                                                    ? `?v=${encodeURIComponent(e.avatarUpdatedAt)}`
                                                                    : ""}`
                                                                : deviceProfile;
                                                            return (
                                                                <tr key={e.id}>
                                                                    <td
                                                                        style={{
                                                                            verticalAlign: "middle",
                                                                            whiteSpace: "nowrap",
                                                                        }}
                                                                    >
                                                                        <img
                                                                            className="user-photo"
                                                                            src={avatarSrc}
                                                                            alt={e.displayName}
                                                                            onError={(ev) => {
                                                                                ev.currentTarget.src = deviceProfile;
                                                                            }}
                                                                        />
                                                                        {e.displayName || "-"}
                                                                    </td>
                                                                    <td style={{ verticalAlign: "middle" }}>
                                                                        {e.entryType === "video_contact"
                                                                            ? "Video"
                                                                            : "Phone"}
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            verticalAlign: "middle",
                                                                            fontFamily: "monospace",
                                                                            fontSize: 12,
                                                                        }}
                                                                    >
                                                                        {e.phoneNumberRaw || "-"}
                                                                    </td>
                                                                    <td style={{ verticalAlign: "middle" }}>
                                                                        <button
                                                                            type="button"
                                                                            className="btn cur-p btn-outline-dark btn-sm"
                                                                            disabled={copyingId === e.id}
                                                                            onClick={() => handleCopyToHome(e)}
                                                                        >
                                                                            {copyingId === e.id
                                                                                ? "Adding..."
                                                                                : "Add to Home Page"}
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default SharedAddressBooksView;
